import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';

interface EditAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  name: string;
  phone: string;
}

export function EditAccountDialog({ open, onOpenChange }: EditAccountDialogProps) {
  const { t } = useTranslation();
  const me = hooks.useMe();
  const update = hooks.useUpdateProfile();
  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: { name: '', phone: '' },
  });

  useEffect(() => {
    if (me.data && open) {
      reset({ name: me.data.name ?? '', phone: me.data.phone ?? '' });
    }
  }, [me.data, open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({ name: values.name, phone: values.phone || undefined });
      toast.success(t('portal.account.profileUpdated'));
      void me.refetch();
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.account.editAccount')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="account-email">{t('auth.email')}</Label>
            <Input id="account-email" value={me.data?.email ?? ''} disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account-name">{t('user.name')}</Label>
            <Input id="account-name" {...register('name', { required: true })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account-phone">{t('auth.phone')}</Label>
            <Input id="account-phone" {...register('phone')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || update.isPending}>
              {t('actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
